import { OrderStateEnum } from '@portal/schema';

export type DisposalOrder =
  | {
      state: OrderStateEnum;
    }
  | null
  | undefined;

/**
 * Check if a disposal order is in the Unconfirmed state
 */
export const isDisposalUnconfirmed = (disposal: DisposalOrder): boolean => {
  return disposal?.state === OrderStateEnum.Unconfirmed;
};

/**
 * Check if a disposal order is in the Confirmed state
 */
export const isDisposalConfirmed = (disposal: DisposalOrder): boolean => {
  return disposal?.state === OrderStateEnum.Confirmed;
};

/**
 * Check if a disposal order is in the Completed state
 */
export const isDisposalCompleted = (disposal: DisposalOrder): boolean => {
  return disposal?.state === OrderStateEnum.Completed;
};

/**
 * Get a human-readable status text for a child disposal order
 */
export const getChildDisposalStatusText = (disposal: DisposalOrder, parentOrderEnded: boolean): string => {
  if (!disposal) return 'Not Requested';
  if (isDisposalUnconfirmed(disposal) && parentOrderEnded) return 'Pending Confirmation';
  if (isDisposalConfirmed(disposal)) return 'Processing';
  if (isDisposalCompleted(disposal)) return 'Completed';
  return 'Requested';
};

/**
 * Check if disposal attach modal can be opened
 * Returns false if:
 * - Disposal is confirmed (processing) or completed - no further action needed
 * - Parent order has ended and there's no document hub URL - stuck state
 * Returns true if user can:
 * - Add/edit disposal (before order)
 * - Estimate costs (during order, read-only)
 * - Confirm items via document hub (after order)
 */
export const allowsOpeningDisposalAttachModal = (
  disposal: (DisposalOrder & { documentHubURL?: string | null }) | null | undefined,
  parentOrderEnded: boolean,
): boolean => {
  // Don't show if disposal is processing or completed
  if (isDisposalConfirmed(disposal) || isDisposalCompleted(disposal)) {
    return false;
  }
  // Don't show if order ended and there's no document hub URL (stuck state)
  if (parentOrderEnded && !disposal?.documentHubURL) {
    return false;
  }
  return true;
};
